import { Typography, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import { fetchLaborLawsForDisplay } from '../../../utils/helpers/laborLaws/laborLawsHelpers';
import { LaborLawsContainer, LaborLawsGrid, LawsList, LawItem } from './LaborLaws.styles';

const LaborLaws = () => {
  const [laborLawsData, setLaborLawsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentVenue = useSelector(selectCurrentVenue);
  const venueId = currentVenue?.id;

  // Fetch labor laws when venue changes
  useEffect(() => {
    const fetchLaborLaws = async () => {
      if (!venueId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchLaborLawsForDisplay(venueId);
        setLaborLawsData(data);
      } catch (err) {
        console.error('Error fetching labor laws:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLaborLaws();
  }, [venueId]);

  // Show loading state
  if (isLoading) {
    return (
      <LaborLawsContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <CircularProgress />
        </div>
      </LaborLawsContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <LaborLawsContainer>
        <Typography variant="h6" color="error">
          Error Loading Labor Laws
        </Typography>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </LaborLawsContainer>
    );
  }

  // Show no venue state
  if (!venueId) {
    return (
      <LaborLawsContainer>
        <Typography variant="body2">
          No venue selected. Please select a venue to view labor laws.
        </Typography>
      </LaborLawsContainer>
    );
  }

  // Show no labor laws state
  if (!laborLawsData || !laborLawsData.laws || laborLawsData.laws.length === 0) {
    return (
      <LaborLawsContainer>
        <Typography variant="h6">
          {laborLawsData?.stateName || 'Unknown State'} Labor Laws
        </Typography>
        <Typography variant="subtitle2">
          <em>Labor laws are maintained by Tempo and are subject to change. Please verify with your legal counsel.</em>
        </Typography>
        <Typography variant="body2">
          Labor law information is not available for this state.
        </Typography>
      </LaborLawsContainer>
    );
  }

  return (
    <LaborLawsContainer>
      <Typography variant="h6">
        {laborLawsData.stateName} Labor Laws
      </Typography>
      <Typography variant="subtitle2">
        <em>Labor laws are maintained by Tempo and are subject to change. Please verify with your legal counsel.</em>
      </Typography>
      <LaborLawsGrid>
        <LawsList>
          {laborLawsData.laws.map(({ title, description }, index) => (
            <LawItem key={`${title}-${index}`}>
              <Typography variant="body2" component="span" fontWeight="bold">
                {title}
              </Typography>
              <Typography variant="body2" component="span">
                {description}
              </Typography>
            </LawItem>
          ))}
        </LawsList>
      </LaborLawsGrid>
    </LaborLawsContainer>
  );
};

export default LaborLaws;
